import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../services/api";
import { set, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { LockKeyhole, SendHorizonal } from "lucide-react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useDispatch } from "react-redux";
import { resetPassword } from "../../State/Auth/Action";
import { useSelector } from "react-redux";

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const { resetPasswordResult, resetPasswordLoading, resetPasswordError } =
    useSelector((state) => state.auth);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
  });

  const password = watch("password");

  const handleResetPassword = async (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const token = searchParams.get("token");
    setLoading(true);
    const input = {
      token,
      newPassword: data.password,
    };
    dispatch(resetPassword(input));
    setLoading(false);
  };

  useEffect(() => {
    if (resetPasswordResult?.success) {
      toast.success("Password reset successful! You can now log in.");
      reset();
    } else if (resetPasswordError?.message) {
      toast.error(
        resetPasswordError.message ||
          "Failed to reset password. Please try again."
      );
      reset();
    }
  }, [resetPasswordResult, resetPasswordError, reset]);

  return (
    <div className="min-h-[calc(100vh-74px)] flex items-center justify-center bg-gradient-to-br from-sky-200 via-pink-100 to-pink-200">
      <motion.form
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        onSubmit={handleSubmit(handleResetPassword)}
        className="w-full max-w-md bg-white/80 backdrop-blur-md shadow-xl rounded-2xl px-8 py-10 flex flex-col gap-5"
      >
        <div className="flex flex-col items-center text-center">
          <LockKeyhole className="w-12 h-12 text-customRed mb-2 animate-bounce" />
          <h1 className="text-2xl font-extrabold text-gray-800 font-montserrat">
            Reset Your Password
          </h1>
          <p className="text-gray-600 text-sm">
            Create a new strong password below.
          </p>
        </div>

        <TextField
          label="New Password"
          type="password"
          fullWidth
          variant="outlined"
          error={!!errors.password}
          helperText={errors.password?.message}
          {...register("password", {
            required: "Password is required",
            minLength: {
              value: 6,
              message: "Minimum 6 characters",
            },
            maxLength: {
              value: 20,
              message: "Maximum 20 characters",
            },
            pattern: {
              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#@$&]).{6,20}$/,
              message:
                "Must include 1 upper, 1 lower, 1 number, and 1 special (#@$&)",
            },
          })}
        />

        <TextField
          label="Confirm Password"
          type="password"
          fullWidth
          variant="outlined"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          {...register("confirmPassword", {
            required: "Confirm Password is required",
            validate: (value) => value === password || "Passwords do not match",
          })}
        />

        <Button
          variant="contained"
          color="error"
          disabled={loading}
          type="submit"
          fullWidth
          sx={{
            borderRadius: "9999px",
            py: 1.5,
            fontWeight: "bold",
            boxShadow: 3,
            textTransform: "none",
          }}
          startIcon={!loading ? <SendHorizonal /> : null}
        >
          {loading ? "Loading..." : "Submit"}
        </Button>

        <p className="text-sm text-gray-700 text-center">
          <Link
            to="/login"
            className="underline hover:text-customRed transition-colors"
          >
            Back to Login
          </Link>
        </p>
      </motion.form>
    </div>
  );
};

export default ResetPassword;
